import { supabase } from '../lib/supabase';

export interface AuditActivity {
  id: string;
  quoteId: string;
  userId: string | null;
  actionType: 'QUOTE_CREATED' | 'QUOTE_EDITED' | 'QUOTE_APPROVED' | 'QUOTE_REJECTED' | 'QUOTE_CANCELLED' | 'EMAIL_SENT';
  actionTimestamp: string;
  userEmail: string | null;
  userName: string | null;
  userRole: string | null;
  quoteReference: string | null;
  customerName: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  changesMade: any;
  notes: string | null;
  createdAt: string;
}

export interface AuditFilters {
  startDate?: string;
  endDate?: string;
  actionType?: string;
  userId?: string;
  quoteReference?: string;
  searchQuery?: string;
}

class AuditService {
  /**
   * Fetch audit activities with optional filters
   */
  async getAuditActivities(filters?: AuditFilters): Promise<AuditActivity[]> {
    try {
      let query = supabase
        .from('quote_activity_audit')
        .select('*')
        .order('action_timestamp', { ascending: false });

      // Apply filters
      if (filters?.startDate) {
        query = query.gte('action_timestamp', filters.startDate);
      }

      if (filters?.endDate) {
        query = query.lte('action_timestamp', filters.endDate);
      }

      if (filters?.actionType && filters.actionType !== 'ALL') {
        query = query.eq('action_type', filters.actionType);
      }

      if (filters?.userId) {
        query = query.eq('user_id', filters.userId);
      }

      if (filters?.quoteReference) {
        query = query.ilike('quote_reference', `%${filters.quoteReference}%`);
      }

      if (filters?.searchQuery) {
        query = query.or(
          `user_name.ilike.%${filters.searchQuery}%,` +
          `user_email.ilike.%${filters.searchQuery}%,` +
          `quote_reference.ilike.%${filters.searchQuery}%,` +
          `customer_name.ilike.%${filters.searchQuery}%,` +
          `notes.ilike.%${filters.searchQuery}%`
        );
      }

      const { data, error } = await query;

      if (error) throw error;

      return (data || []).map(this.mapToAuditActivity);
    } catch (error) {
      console.error('Error fetching audit activities:', error);
      throw error;
    }
  }

  /**
   * Get audit activities for a specific quote
   */
  async getQuoteAuditHistory(quoteId: string): Promise<AuditActivity[]> {
    try {
      const { data, error } = await supabase
        .from('quote_activity_audit')
        .select('*')
        .eq('quote_id', quoteId)
        .order('action_timestamp', { ascending: false });

      if (error) throw error;

      return (data || []).map(this.mapToAuditActivity);
    } catch (error) {
      console.error('Error fetching quote audit history:', error);
      throw error;
    }
  }

  /**
   * Log email send activity
   */
  async logEmailSend(quoteId: string, recipientEmail: string, notes?: string): Promise<void> {
    try {
      const { error } = await supabase.rpc('log_quote_email_send', {
        p_quote_id: quoteId,
        p_recipient_email: recipientEmail,
        p_notes: notes || null
      });

      if (error) throw error;
    } catch (error) {
      console.error('Error logging email send:', error);
      throw error;
    }
  }

  /**
   * Export audit data to CSV
   */
  async exportToCsv(filters?: AuditFilters): Promise<string> {
    try {
      const activities = await this.getAuditActivities(filters);

      const headers = [
        'Timestamp',
        'Action Type',
        'User Name',
        'User Email',
        'User Role',
        'Quote Reference',
        'Customer Name',
        'Notes',
        'IP Address'
      ];

      const csvRows = [
        headers.join(','),
        ...activities.map(activity => [
          new Date(activity.actionTimestamp).toLocaleString(),
          activity.actionType,
          activity.userName || 'N/A',
          activity.userEmail || 'N/A',
          activity.userRole || 'N/A',
          activity.quoteReference || 'N/A',
          activity.customerName || 'N/A',
          (activity.notes || '').replace(/,/g, ';'),
          activity.ipAddress || 'N/A'
        ].map(field => `"${field}"`).join(','))
      ];

      return csvRows.join('\n');
    } catch (error) {
      console.error('Error exporting audit data:', error);
      throw error;
    }
  }

  /**
   * Get activity statistics
   */
  async getActivityStats(startDate?: string, endDate?: string): Promise<{
    totalActivities: number;
    activitiesByType: Record<string, number>;
    activitiesByUser: Record<string, number>;
  }> {
    try {
      const filters: AuditFilters = {};
      if (startDate) filters.startDate = startDate;
      if (endDate) filters.endDate = endDate;

      const activities = await this.getAuditActivities(filters);

      const activitiesByType: Record<string, number> = {};
      const activitiesByUser: Record<string, number> = {};

      activities.forEach(activity => {
        // Count by action type
        activitiesByType[activity.actionType] = (activitiesByType[activity.actionType] || 0) + 1;

        // Count by user
        const userKey = activity.userName || 'System';
        activitiesByUser[userKey] = (activitiesByUser[userKey] || 0) + 1;
      });

      return {
        totalActivities: activities.length,
        activitiesByType,
        activitiesByUser
      };
    } catch (error) {
      console.error('Error getting activity stats:', error);
      throw error;
    }
  }

  /**
   * Map database record to AuditActivity interface
   */
  private mapToAuditActivity(record: any): AuditActivity {
    return {
      id: record.id,
      quoteId: record.quote_id,
      userId: record.user_id,
      actionType: record.action_type,
      actionTimestamp: record.action_timestamp,
      userEmail: record.user_email,
      userName: record.user_name,
      userRole: record.user_role,
      quoteReference: record.quote_reference,
      customerName: record.customer_name,
      ipAddress: record.ip_address,
      userAgent: record.user_agent,
      changesMade: record.changes_made,
      notes: record.notes,
      createdAt: record.created_at
    };
  }
}

export const auditService = new AuditService();